/**
 * Block rendering (`specs/M11-docs.md` §2, §6).
 *
 * One renderer per block type, chosen from a map — not a chain of `if`s (CLAUDE.md rule 6).
 * The thirteen types that occur in the account's 20 docs all render, and so do `quote` and
 * `code`, which monday's API can create but no imported doc uses.
 *
 * The delta is read, never executed: `deltaToRuns` turns it into a flat list of styled runs
 * and each run becomes one `<span>`. A `link` attribute becomes an anchor with
 * `rel="noreferrer"`, because the text came out of somebody's document.
 */
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { DocBlockNode, TextRun } from '@elizmonday/shared';
import { deltaToRuns, tableGrid } from '@elizmonday/shared';

function runStyle(run: TextRun): React.CSSProperties {
  return {
    ...(run.bold ? { fontWeight: 700 } : {}),
    ...(run.italic ? { fontStyle: 'italic' } : {}),
    ...(run.underline || run.strike
      ? {
          textDecoration: [run.underline ? 'underline' : null, run.strike ? 'line-through' : null]
            .filter(Boolean)
            .join(' '),
        }
      : {}),
    ...(run.color === null ? {} : { color: run.color }),
    ...(run.background === null ? {} : { backgroundColor: run.background }),
    ...(run.fontSize === null ? {} : { fontSize: `${run.fontSize}px` }),
  };
}

export function Runs({ node }: { node: DocBlockNode }) {
  const runs = deltaToRuns(node.content);
  if (runs.length === 0) return null;
  return (
    <>
      {runs.map((run, index) =>
        run.link === null ? (
          <span key={`${index}-${run.text.slice(0, 8)}`} style={runStyle(run)}>
            {run.text}
          </span>
        ) : (
          <a
            key={`${index}-${run.link}`}
            href={run.link}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="doc-run-link"
            style={runStyle(run)}
          >
            {run.text}
          </a>
        ),
      )}
    </>
  );
}

export interface BlockProps {
  node: DocBlockNode;
  /** Index within its list run, for a numbered list's ordinal. */
  ordinal?: number;
  onToggleCheck?: ((node: DocBlockNode, checked: boolean) => void) | undefined;
  onEditText?: ((node: DocBlockNode, text: string) => void) | undefined;
  readOnly?: boolean;
  /**
   * DOM attributes for the block's **outer** element — the one that carries `data-block-id` and
   * is therefore the drag source (P-185). They are a prop rather than a `cloneElement` on the
   * result, because several block kinds return a *component* (`<Editable>`) whose props would
   * never reach the DOM. A nested `Editable` inside a list row never receives them.
   */
  dom?: React.HTMLAttributes<HTMLElement> & { draggable?: boolean };
  /** True while this block is the one being edited: see `Editable` (P-185). */
  active?: boolean;
  /** Ask the document to make this block the edited one. */
  onActivate?: (() => void) | undefined;
}

/**
 * A text-bearing block: editable in place when the caller supplies `onEditText`.
 *
 * **`contentEditable` is applied on activation, not always** (P-185). A permanently editable
 * element is an *editing host*, and Chromium gives an editing host's `mousedown` to the text
 * selection rather than to the drag controller — so a block that was always `contentEditable`
 * could carry `draggable="true"` and still never start a drag, which is why a doc block could
 * not be moved at all. Clicking (or tabbing to) a block activates it, the effect below puts the
 * caret at the end, and from that moment it behaves exactly as it did before: type, select with
 * the mouse, blur to commit. An **inactive** block is a plain node, so the row's drag works.
 */
function Editable({
  node,
  onEditText,
  readOnly,
  className,
  tag,
  dom,
  active = false,
  onActivate,
}: BlockProps & { className: string; tag: keyof React.JSX.IntrinsicElements }) {
  const Tag = tag as 'p';
  const editable = !readOnly && onEditText !== undefined;
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!editable || !active || element === null) return;
    if (document.activeElement === element) return;
    element.focus();
    // The caret goes where a reader expects it after clicking into a paragraph: at the end.
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, [editable, active]);

  return (
    <Tag
      ref={ref as React.Ref<HTMLParagraphElement>}
      className={className}
      data-block-id={node.id}
      data-block-type={node.type}
      {...(dom ?? {})}
      {...(editable
        ? {
            ...(active ? { contentEditable: true, suppressContentEditableWarning: true } : { tabIndex: 0 }),
            role: 'textbox',
            onMouseDown: () => onActivate?.(),
            onFocus: () => onActivate?.(),
            // Blur, not input: one write per edit instead of one per keystroke.
            onBlur: (event: React.FocusEvent<HTMLElement>) => {
              onEditText(node, event.currentTarget.innerText.replace(/\n+$/, ''));
            },
          }
        : {})}
    >
      <Runs node={node} />
    </Tag>
  );
}

export function DocBlock(props: BlockProps): React.JSX.Element | null {
  const { node, ordinal, onToggleCheck, readOnly, dom } = props;
  /** The inner text of a list row is not the block's outer element: it never takes the drag. */
  const inner = { ...props, dom: undefined };

  switch (node.type) {
    case 'large title':
      return <Editable {...props} tag="h1" className="ez-doc-block ez-doc-block--h1" />;
    case 'medium title':
      return <Editable {...props} tag="h2" className="ez-doc-block ez-doc-block--h2" />;
    case 'small title':
      return <Editable {...props} tag="h3" className="ez-doc-block ez-doc-block--h3" />;
    case 'quote':
      return <Editable {...props} tag="blockquote" className="ez-doc-block ez-doc-block--quote" />;
    case 'code':
      return <Editable {...props} tag="pre" className="ez-doc-block ez-doc-block--code" />;

    case 'bulleted list':
      return (
        <div
          className="ez-doc-block ez-doc-block--bullet"
          data-block-id={node.id}
          data-block-type={node.type}
          style={{ paddingLeft: 24 * (1 + (node.content.indentation ?? 0)) }}
          {...(dom ?? {})}
        >
          <span className="ez-doc-marker" aria-hidden="true">
            •
          </span>
          <Editable {...inner} tag="span" className="ez-doc-text" />
        </div>
      );

    case 'numbered list':
      return (
        <div
          className="ez-doc-block ez-doc-block--number"
          data-block-id={node.id}
          data-block-type={node.type}
          style={{ paddingLeft: 24 * (1 + (node.content.indentation ?? 0)) }}
          {...(dom ?? {})}
        >
          <span className="ez-doc-marker" aria-hidden="true">
            {(ordinal ?? 0) + 1}.
          </span>
          <Editable {...inner} tag="span" className="ez-doc-text" />
        </div>
      );

    case 'check list':
      return (
        <div
          className="ez-doc-block ez-doc-block--check"
          data-block-id={node.id}
          data-block-type={node.type}
          {...(dom ?? {})}
        >
          <input
            type="checkbox"
            checked={node.content.checked === true}
            disabled={readOnly === true || onToggleCheck === undefined}
            data-testid={`doc-check-${node.id}`}
            aria-label="Done"
            onChange={(event) => onToggleCheck?.(node, event.target.checked)}
          />
          <Editable {...inner} tag="span" className="ez-doc-text" />
        </div>
      );

    case 'divider':
      return (
        <hr className="ez-doc-block ez-doc-block--divider" data-block-id={node.id} data-block-type={node.type} {...(dom ?? {})} />
      );

    case 'image': {
      const source = node.content.publicUrl ?? node.content.url;
      if (typeof source !== 'string' || source === '') {
        return (
          <div className="ez-doc-block ez-doc-block--placeholder" data-block-id={node.id} data-block-type={node.type} {...(dom ?? {})}>
            Image (no source stored)
          </div>
        );
      }
      return (
        <figure className="ez-doc-block ez-doc-block--image" data-block-id={node.id} data-block-type={node.type} {...(dom ?? {})}>
          <img
            src={source}
            alt=""
            style={
              typeof node.content.widthPercentage === 'number'
                ? { width: `${node.content.widthPercentage}%` }
                : typeof node.content.width === 'number'
                  ? { width: node.content.width }
                  : undefined
            }
          />
        </figure>
      );
    }

    case 'file':
      return (
        <div className="ez-doc-block ez-doc-block--file" data-block-id={node.id} data-block-type={node.type} {...(dom ?? {})}>
          📎 {typeof node.content.url === 'string' ? node.content.url : 'File'}
        </div>
      );

    case 'board':
      return <BoardEmbed {...props} />;

    case 'table': {
      const grid = tableGrid(node);
      if (grid.length === 0) return null;
      return (
        <table className="ez-doc-block ez-doc-block--table" data-block-id={node.id} data-block-type={node.type} {...(dom ?? {})}>
          <tbody>
            {grid.map((row, rowIndex) => (
              <tr key={row.map((cell) => cell?.id ?? '_').join('|') || String(rowIndex)}>
                {row.map((cell, cellIndex) => (
                  <td key={cell?.id ?? `empty-${cellIndex}`} data-block-id={cell?.id}>
                    {(cell?.children ?? []).map((child) => (
                      <DocBlock key={child.id} {...inner} node={child} />
                    ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    case 'cell':
      // Rendered by its table; on its own it is not a block a reader sees.
      return null;

    default:
      return <Editable {...props} tag="p" className="ez-doc-block ez-doc-block--text" />;
  }
}

/**
 * A `board` block (**P-185**, E8).
 *
 * M11 §2.1 named the board it could not draw — *"Embedded board · widget 12345"* — because an
 * empty frame reads as a broken doc, and that was the right call while nothing could resolve the
 * board behind monday's stored `widgetId`. `docs.boardEmbed` now does: a widget belongs to a
 * board view or to a dashboard, and either answers a board. So the block is a **frame** with the
 * board in it, and it keeps the honest caption for the one case that has no answer — a widget
 * that never came across with the import, or a board this reader cannot open.
 *
 * The frame is an `<iframe>` of the board's own route rather than a second board renderer: a doc
 * embed that drifted from the board screen would be worse than no embed. What monday draws, and
 * at what height, is **q538** — the 420 px here is ours and says so.
 */
function BoardEmbed({ node, dom }: BlockProps) {
  const widgetId = node.content.widgetId === undefined ? null : String(node.content.widgetId);
  const resolver = useDocEmbedResolver();
  const [target, setTarget] = useState<{ boardId: string; boardName: string } | null | undefined>(undefined);

  useEffect(() => {
    if (widgetId === null || resolver === null) {
      setTarget(null);
      return;
    }
    let cancelled = false;
    void resolver(widgetId)
      .then((answer) => {
        if (!cancelled) setTarget(answer);
      })
      .catch(() => {
        if (!cancelled) setTarget(null);
      });
    return () => {
      cancelled = true;
    };
  }, [widgetId, resolver]);

  return (
    <div
      className="ez-doc-block ez-doc-block--embed"
      data-block-id={node.id}
      data-block-type={node.type}
      data-testid="board-embed"
      {...(dom ?? {})}
    >
      {target === undefined ? (
        <span>Loading the embedded board…</span>
      ) : target === null ? (
        <>
          <strong>Embedded board</strong>
          <span>{`widget ${widgetId ?? '?'} is not in this account`}</span>
        </>
      ) : (
        <>
          <strong>{target.boardName}</strong>
          <iframe
            title={`${target.boardName} (embedded)`}
            src={`/boards/${target.boardId}`}
            className="ez-doc-embed__frame"
            loading="lazy"
          />
        </>
      )}
    </div>
  );
}

/**
 * How a `board` block resolves its embed. The renderer is used by the doc screen, by the harness
 * and by tests, and only one of the three has a tRPC client — so the resolver is injected through
 * a context rather than imported, the same rule every other module in this app follows.
 */
export type DocEmbedResolver = (widgetId: string) => Promise<{ boardId: string; boardName: string } | null>;

const DocEmbedContext = createContext<DocEmbedResolver | null>(null);

export function DocEmbedProvider({ resolve, children }: { resolve: DocEmbedResolver; children: React.ReactNode }) {
  return <DocEmbedContext.Provider value={resolve}>{children}</DocEmbedContext.Provider>;
}

function useDocEmbedResolver(): DocEmbedResolver | null {
  return useContext(DocEmbedContext);
}

/**
 * The whole document. A numbered list restarts at 1 after any other block, which is what
 * monday does and what the stored `indentation` alone cannot express.
 *
 * ## Block chrome (**P-185**)
 *
 * Until round 10k a doc could only ever grow: no block could be **moved** (no handle, no
 * `draggable`, no reorder procedure) and none could be **deleted** (`docs.deleteBlock` existed,
 * was tested, and had no caller anywhere in the web app), so a block written in the wrong place
 * was permanent. Each block now carries a hover gutter with a `⠿` and a `🗑`, and is itself the
 * drag source — the element that already carried `data-block-id`, so no second node appears in
 * the document for a verifier or a reader to trip over.
 *
 * `draggable` is **off while the block has focus**: a block is a `contenteditable`, and a
 * permanently draggable editing host cannot be text-selected with the mouse. Clicking into a
 * block focuses it (a click is not a drag) and from then on it selects text like any paragraph.
 *
 * What monday's own handle looks like, and whether its menu carries more than these two rows, is
 * **q538** — not captured on this account. This is the smallest chrome that closes the defect.
 */
export function DocBlocks(props: {
  nodes: readonly DocBlockNode[];
  onToggleCheck?: ((node: DocBlockNode, checked: boolean) => void) | undefined;
  onEditText?: ((node: DocBlockNode, text: string) => void) | undefined;
  /** Reorder: `node` lands where `target` is now. Absent = the doc is not reorderable. */
  onMove?: ((node: DocBlockNode, targetId: string) => void) | undefined;
  onDelete?: ((node: DocBlockNode) => void) | undefined;
  readOnly?: boolean;
}) {
  const [focused, setFocused] = useState<string | null>(null);
  /*
   * A **ref**, not state: the browser's drag controller can dispatch `dragstart` and the first
   * `dragover` in one task, and React has not committed a `setState` by then — the drop would
   * read `null` and refuse itself. `WidgetCanvas` holds its grip drag the same way.
   */
  const dragging = useRef<string | null>(null);
  const writable = props.readOnly !== true;
  const movable = writable && props.onMove !== undefined;

  let ordinal = 0;
  return (
    <>
      {props.nodes.map((node) => {
        if (node.type === 'numbered list') ordinal += 1;
        else ordinal = 0;
        const startDrag = (event: React.DragEvent) => {
          dragging.current = node.id;
          event.dataTransfer.effectAllowed = 'move';
          event.dataTransfer.setData('text/plain', node.id);
        };

        const element = DocBlock({
          node,
          ordinal: Math.max(0, ordinal - 1),
          onToggleCheck: props.onToggleCheck,
          onEditText: props.onEditText,
          readOnly: props.readOnly,
          active: focused === node.id,
          onActivate: () => setFocused(node.id),
          dom: {
            draggable: movable,
            ...(movable ? { onDragStart: startDrag } : {}),
            onDragEnd: () => {
              dragging.current = null;
            },
          },
        });
        if (element === null) return null;

        return (
          <div
            key={node.id}
            className="ez-doc-row"
            data-doc-row={node.id}
            data-testid={`doc-block-row-${node.id}`}
            // Capture phase: the block's own `onBlur` (which commits its text) is untouched.
            onFocusCapture={() => setFocused(node.id)}
            onBlurCapture={() => setFocused((current) => (current === node.id ? null : current))}
            onDragOver={(event) => {
              if (!movable || dragging.current === null || dragging.current === node.id) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(event) => {
              if (!movable || dragging.current === null || dragging.current === node.id) return;
              event.preventDefault();
              const moved = props.nodes.find((entry) => entry.id === dragging.current);
              dragging.current = null;
              if (moved !== undefined) props.onMove?.(moved, node.id);
            }}
          >
            {!writable ? null : (
              <div className="ez-doc-row__chrome" aria-hidden={false}>
                <button
                  type="button"
                  className="ez-doc-row__handle"
                  data-testid={`doc-block-drag-${node.id}`}
                  aria-label="Move block"
                  draggable={movable}
                  disabled={!movable}
                  onDragStart={startDrag}
                  onDragEnd={() => {
                    dragging.current = null;
                  }}
                >
                  ⠿
                </button>
                {props.onDelete === undefined ? null : (
                  <button
                    type="button"
                    className="ez-doc-row__delete"
                    data-testid={`doc-block-delete-${node.id}`}
                    aria-label="Delete block"
                    onClick={() => props.onDelete?.(node)}
                  >
                    🗑
                  </button>
                )}
              </div>
            )}
            {element}
          </div>
        );
      })}
    </>
  );
}
